import React from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { theme } from './theme/theme';
import { NotificationManager } from './app/components/NotificationManager';
import { TourProvider } from './app/contexts/TourContext';
import {
    SecurityContextProvider,
    AuthNavigationContainer,
    AuthUIContextProvider,
    useSecurityActions,
} from '@brightlayer-ui/react-auth-workflow';
import { ProjectAuthUIActions } from './actions/AuthUIActions';
import { ProjectRegistrationUIActions } from './actions/RegistrationUIActions';
import { routes } from './constants/routing';
import productLogo from './assets/images/eaton_stacked_logo.png';
import { Route } from 'react-router-dom';
import ResetPassword from './components/ResetPassword/ResetPassword';
import Registration from './components/Registration/Registration';
import AnotherAdopterRegistration from './components/Registration/AnotherAdopterRegistration';
import { Provider } from 'react-redux';
import ErrorHandler from './components/ErrorHandler/ErrorHandler';
import { store } from './store';
import { MainLayout } from './app/components/MainLayout';
import { MainRouter } from './router/main';

export const AuthUIConfiguration: React.FC<React.PropsWithChildren> = (props) => {
    const securityContextActions = useSecurityActions();

    return (
        <AuthUIContextProvider
            authActions={ProjectAuthUIActions(securityContextActions)}
            registrationActions={ProjectRegistrationUIActions}
            showSelfRegistration={false}
            allowDebugMode={false}
            htmlEula={false}
            contactEmail={'something@email.com'}
            contactPhone={'1-800-123-4567'}
            projectImage={productLogo}
        >
            {props.children}
        </AuthUIContextProvider>
    );
};

// Wrapper to filter out Figma-specific props
const FilteredThemeProvider: React.FC<{ theme: any; children: React.ReactNode;[key: string]: any }> = ({ theme, children, ...rest }) => {
    // Filter out Figma-specific props
    const filteredProps = Object.keys(rest).reduce((acc, key) => {
        if (!key.startsWith('data-fg') && !key.startsWith('data-fgid')) {
            acc[key] = rest[key];
        }
        return acc;
    }, {} as any);

    return <ThemeProvider theme={theme} {...filteredProps}>{children}</ThemeProvider>;
};

export const App = (): JSX.Element => (
    <Provider store={store}>
        <FilteredThemeProvider theme={theme}>
            <SecurityContextProvider>
                <AuthUIConfiguration>
                    <TourProvider>
                        <CssBaseline />
                        <ErrorHandler />
                        <AuthNavigationContainer routeConfig={routes} extraRoutes={[
                            <Route key={'resetPassword'} path={'/resetPassword'} element={<ResetPassword />} />,
                            <Route key={'registration'} path={'/registration/:key'} element={<Registration />} />,
                            <Route key={'otherAdopterUserRegistration'} path={'/otherAdopterUserRegistration/:key'} element={<AnotherAdopterRegistration />} />
                        ]}>
                            <Route path={''} element={<MainLayout />}>
                                {MainRouter}
                            </Route>
                        </AuthNavigationContainer>
                        <NotificationManager />
                    </TourProvider>
                </AuthUIConfiguration>
            </SecurityContextProvider>
        </FilteredThemeProvider>
    </Provider>
);